<template>
  <b-row no-gutters>
    <b-col cols="12" class="border-bottom">
      <h5 class="font-weight-bold p-2 mb-0 text-center bg-light">
        {{ subject }}
      </h5>
    </b-col>
    <b-col cols="12" class="p-2">
      <!-- INTRO -->
      <intro
        class="mb-2"
        :name="owner.firstName"
        :text="
          $t('emails.ownerGuestsReport.intro', locale, {
            clientName: client.fullName,
            localizator: booking.localizator,
            accommodationName: accommodation.name,
          })
        "
      />

      <!-- PICTURE && ACCOMMODATION INFO -->
      <accommodation-info
        :accommodation="accommodation"
        booking-status="CONFIRMED"
        :locale="locale"
      />

      <b-divider size="lg" />

      <!-- CHECK-IN AND CHECKOUT TIMES -->
      <checkin-checkout
        :checkin="checkinDay"
        :checkout="checkoutDay"
        :locale="locale"
        hide-times
      />

      <b-divider size="lg" />

      <!-- LOCALIZATOR -->
      <localizator :locale="locale" />

      <b-divider size="lg" />

      <!-- GUESTS -->
      <guests-description
        :alert-text="$t('emails.common.attachedGuestsReport', locale)"
        :locale="locale"
      />
    </b-col>
  </b-row>
</template>

<script>
import { BRow, BCol } from "bootstrap-vue";
import BDivider from "@foravila-core/components/b-divider/BDivider.vue";
import Intro from "@/views/bookings/booking/components/mail/Intro.vue";
import AccommodationInfo from "@/views/bookings/booking/components/mail/AccommodationInfo.vue";
import CheckinCheckout from "@/views/bookings/booking/components/mail/CheckinCheckout.vue";
import GuestsDescription from "@/views/bookings/booking/components/mail/GuestsDescription.vue";
import Localizator from "@/views/bookings/booking/components/mail/Localizator.vue";
import { formatDateStringToDate } from "@/utils/formatters";
import {
  getAccommodationTypeName,
  getAdultsText,
  getBabiesText,
  getChildrensText,
} from "@/utils/methods";

export default {
  components: {
    BRow,
    BCol,
    BDivider,
    Intro,
    AccommodationInfo,
    CheckinCheckout,
    GuestsDescription,
    Localizator,
  },
  props: {
    locale: {
      type: String,
      default: "en-GB",
    },
  },
  data() {
    return {
      content: {},
    };
  },
  computed: {
    booking() {
      return this.$store.getters["booking/booking"];
    },
    accommodation() {
      return this.$store.getters["accommodation/accommodation"];
    },
    accommodationPicture() {
      return this.accommodation.coverPicture || null;
    },
    accommodationType() {
      if (!this.accommodation) return null;
      return getAccommodationTypeName(this.accommodation.type, this.locale);
    },
    accommodationLocation() {
      if (!this.accommodation?.location) return null;

      const location = [];
      if (this.accommodation.location.city) { location.push(this.accommodation.location.city); }
      if (this.accommodation.location.region) { location.push(this.accommodation.location.region); }
      return location.length ? location.join(", ") : null;
    },
    owner() {
      return this.$store.getters["booking/owner"];
    },
    client() {
      return this.$store.getters["booking/client"];
    },
    bookingDate() {
      return formatDateStringToDate(this.booking?.date, this.locale);
    },
    checkinDay() {
      const formatting = {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      };
      return formatDateStringToDate(
        this.booking.checkin,
        this.locale,
        formatting
      );
    },
    checkoutDay() {
      const formatting = {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      };
      return formatDateStringToDate(
        this.booking.checkout,
        this.locale,
        formatting
      );
    },
    checkin() {
      return formatDateStringToDate(this.booking.checkin, this.locale);
    },
    checkout() {
      return formatDateStringToDate(this.booking.checkout, this.locale);
    },
    guests() {
      return this.$store.getters["booking/guests"];
    },
    adults() {
      return this.$store.getters["booking/adults"];
    },
    children() {
      return this.$store.getters["booking/children"];
    },
    babies() {
      return this.$store.getters["booking/babies"];
    },
    adultsText() {
      return getAdultsText(this.booking.adults, this.locale);
    },
    childrenText() {
      return getChildrensText(this.booking.children, this.locale);
    },
    babiesText() {
      return getBabiesText(this.booking.babies, this.locale);
    },
    adultsAgesText() {
      if (this.adults.length !== this.booking.adults) {
        return this.$t("AnysEdatNoConfirmats", this.locale);
      }

      const text = [];
      this.adults.forEach((guest) => {
        text.push(guest.age);
      });

      return `${text.join(", ")} ${this.$t(
        "AnysEdat",
        this.locale
      ).toLowerCase()}`;
    },
    childrenAgesText() {
      if (this.booking.children === 0) {
        return "";
      }

      if (this.children.length !== this.booking.children) {
        return this.$t("AnysEdatNoConfirmats", this.locale);
      }

      const text = [];
      this.children.forEach((guest) => {
        text.push(guest.age);
      });

      return `${text.join(", ")} ${this.$t(
        "AnysEdat",
        this.locale
      ).toLowerCase()}`;
    },
    babiesAgesText() {
      if (this.booking.babies === 0) {
        return "";
      }

      if (this.babies.length !== this.booking.babies) {
        return this.$t("AnysEdatNoConfirmats", this.locale);
      }

      const text = [];
      this.babies.forEach((guest) => {
        text.push(guest.age);
      });

      return `${text.join(", ")} ${this.$t(
        this.babies.length === 1 && this.babies[0].age === 1 ? "AnysEdatSingular" : "AnysEdat",
        this.locale
      ).toLowerCase()}`;
    },
    reportableGuests() {
      return this.$store.getters["booking/reportableGuests"];
    },
    formattedReportableGuests() {
      // Format the dates so they are displayed correctly in the generated pdf
      if (!this.reportableGuests.length) return [];
      return this.reportableGuests.map((guest) => ({
        ...guest,
        birthDate: formatDateStringToDate(guest.birthDate),
        idIssueDate: formatDateStringToDate(guest.idIssueDate),
      }));
    },
    subject() {
      return `${this.$t("emails.ownerGuestsReport.subject", this.locale, {
        localizator: this.booking.localizator,
      })}`;
    },
  },
  watch: {
    content: {
      deep: true,
      handler() {
        this.$emit("content-updated", this.content);
      },
    },
  },
  created() {
    this.initInputs();
  },
  methods: {
    initInputs() {
      this.content = {
        locale: this.locale,
        subject: this.subject,
        ownerEmail: this.owner?.email || null,
        ownerSecondaryEmail: this.owner?.secondaryEmail || null,
        ownerName: this.owner?.firstName || null,
        clientName: this.client?.firstName || null,
        clientEmail: this.client?.email || null,
        accommodationPicture: this.accommodationPicture?.url || null,
        accommodationDescription: this.accommodationType,
        accommodationName: this.accommodation?.name || null,
        accommodationLocation: this.accommodationLocation,
        accommodationCity: this.accommodation?.location?.city || null,
        accommodationAddress: this.accommodation?.location?.fullAddress || null,
        accommodationHospedajesCode: this.accommodation?.hospedajesAccommodationCode || null,
        accommodationTouristLicenceNumber: this.accommodation?.touristLicenceNumber || null,
        bookingDate: this.bookingDate || null,
        checkin: this.checkin,
        checkout: this.checkout,
        checkinDay: this.checkinDay,
        checkoutDay: this.checkoutDay,
        adults: this.booking?.adults || null,
        adultsAges: this.adultsAgesText || null,
        children: this.booking?.children || 0,
        childrenAges: this.childrenAgesText || null,
        babies: this.booking?.babies || null,
        babiesAges: this.babiesAgesText || null,
        localizator: this.booking?.localizator || null,
        bookingEmail: this.booking?.email || null,
        reportableGuests: this.reportableGuests.length
          ? this.formattedReportableGuests
          : null,
      };
    },
  },
};
</script>
